import { ReactNode, useLayoutEffect, useRef } from 'react';
import * as Popover from '@radix-ui/react-popover';
import styles from './InquiryAnchoredPopover.module.scss';

interface InquiryAnchoredPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  /** Fixed width in px; omit for intrinsic width (e.g. small success popover) */
  width?: number;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  /** If true, block interaction outside (default false so Blade/table keep working) */
  modal?: boolean;
  contentClassName?: string;
  onOpenAutoFocus?: (e: Event) => void;
  /**
   * False keeps the panel open through clicks, taps and focus changes outside it, leaving Escape
   * and its own controls as the ways out. Default true, which is what a popover should normally do.
   *
   * ⚠️ Needed for a panel that opens by itself rather than by being asked for. Radix closes on the
   * first interaction outside, and page load produces one - so a self-opening panel flashed and
   * vanished before anyone could read it, while still recording itself as shown.
   */
  dismissOnInteractOutside?: boolean;
}

/**
 * Popover positioned to an arbitrary DOM node (e.g. MUI-style anchorEl from a toolbar button).
 */
export default function InquiryAnchoredPopover({
  open,
  anchorEl,
  onClose,
  children,
  width,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  modal = false,
  contentClassName = '',
  onOpenAutoFocus,
  dismissOnInteractOutside = true,
}: InquiryAnchoredPopoverProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!open || !anchorEl || !anchorRef.current) return;

    const apply = () => {
      const el = anchorRef.current;
      if (!el || !anchorEl) return;
      const r = anchorEl.getBoundingClientRect();
      el.style.position = 'fixed';
      el.style.left = `${r.left}px`;
      el.style.top = `${r.top}px`;
      el.style.width = `${Math.max(r.width, 1)}px`;
      el.style.height = `${Math.max(r.height, 1)}px`;
      el.style.visibility = 'visible';
      el.style.pointerEvents = 'none';
    };

    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(anchorEl);
    window.addEventListener('scroll', apply, true);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', apply, true);
      window.removeEventListener('resize', apply);
      if (anchorRef.current) {
        anchorRef.current.style.visibility = 'hidden';
      }
    };
  }, [open, anchorEl]);

  return (
    <Popover.Root open={open && !!anchorEl} onOpenChange={(v) => { if (!v) onClose(); }} modal={modal}>
      <Popover.Anchor asChild>
        <span ref={anchorRef} className={styles.anchorSlot} aria-hidden />
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          className={`${styles.content} ${contentClassName}`.trim()}
          style={
            width != null
              ? { width, minWidth: width, maxWidth: 'min(100vw - 24px, 420px)' }
              : { minWidth: 200, maxWidth: 'min(100vw - 24px, 360px)' }
          }
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={12}
          onOpenAutoFocus={onOpenAutoFocus}
          onInteractOutside={dismissOnInteractOutside ? undefined : (e) => e.preventDefault()}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
